import { useTheme } from "@mui/material";
import { TooltipComponentOption } from "echarts";
import ReactECharts, { EChartsOption } from "echarts-for-react";

import { colors } from "@/Utils";

export type WaterfallChartDataItem = {
  name: string;
  value: number;
  children?: WaterfallChartDataItem[];
};

type WaterfallChartProps = {
  data: WaterfallChartDataItem[];
  xAxisLabelFormatter: (value: string) => string;
  yAxisLabel?: string;
  yAxisLabelFormatter?: (value: number) => string;
  barLabel?: any;
  tooltipOptions?: TooltipComponentOption;
  total?: number;
  options?: Partial<EChartsOption>;
};

const createTransparentSeriesData = (data: number[], hasTotal: boolean) => {
  const values: number[] = hasTotal ? [0] : [];
  let total = 0;
  data.forEach((value) => {
    if (value > 0) {
      values.push(total);
      total += value;
    } else {
      total += value;
      values.push(total);
    }
  });
  return values;
};

export const WaterfallChart = ({
  data,
  xAxisLabelFormatter,
  yAxisLabel,
  yAxisLabelFormatter,
  barLabel,
  tooltipOptions,
  total,
  options = {},
}: WaterfallChartProps) => {
  const theme = useTheme();

  const chartData = total ? [{ name: "Total Sales", value: total }, ...data] : data;

  const originalData = [...data];

  const baseOption: EChartsOption = {
    grid: {
      left: 4,
      right: 0,
      top: 40,
      bottom: 20,
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
      padding: 0,
      borderWidth: 0,
      borderColor: "transparent",
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: theme.palette.echartTooltip.background,
      textStyle: {
        color: theme.palette.echartTooltip.textColor,
      },
      ...(tooltipOptions ?? {}),
    },
    xAxis: {
      data: chartData.map((item) => item.name),
      axisLabel: {
        color: theme.palette.brandedTypography?.textColor,
        fontSize: 12,
        interval: 0,
        formatter: xAxisLabelFormatter,
      },
    },
    yAxis: {
      type: "value",
      name: yAxisLabel,
      nameTextStyle: {
        color: theme.palette.brandedTypography?.textColor,
        fontWeight: 600,
        fontSize: 14,
        padding: [0, 0, 10, -40],
      },
      axisLabel: {
        fontSize: 14,
        color: theme.palette.brandedTypography?.textColor,
        formatter: yAxisLabelFormatter,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: theme.palette.ChartGuideLines.color,
        },
      },
    },
    series: [
      {
        name: "Placeholder",
        type: "bar",
        stack: "Total",
        itemStyle: {
          color: "transparent",
        },
        minBarLength: 10,
        data: createTransparentSeriesData(
          originalData.map((item) => item.value),
          !!total
        ),
      },
      {
        name: "Sales",
        type: "bar",
        stack: "Total",
        data: chartData.map(({ value }) => Math.abs(value)),
        itemStyle: {
          borderRadius: 4,
          color: (params: any) => {
            if (params.name === "Base Sales") return theme.palette.baseSales.color;
            const index = params.dataIndex;
            const item = chartData[index];
            if (!item) return "transparent";
            if (index === 0 && !!total) return colors.revenuePurple;
            if (item.value > 0) return colors.greenSuccess;
            return colors.redError;
          },
        },
        ...(barLabel ? { label: barLabel } : {}),
      },
    ],
  };

  const option: EChartsOption = {
    ...baseOption,
    ...options,
  };
  return <ReactECharts option={option} style={{ minHeight: "600px" }} notMerge={true} />;
};

export const sortItems = (data: WaterfallChartDataItem[], length: number = 6) => {
  // If we have exactly 1 more than the number of sole categories we want to show, there is no need for 'Other' with one item
  if (data.length <= length + 1) return data;

  const topItems = data.slice(0, length);
  const otherItems = data.slice(length);

  const otherValue = otherItems.reduce((sum, item) => sum + item.value, 0);

  const finalData = [
    ...topItems,
    ...(otherItems.length > 0
      ? [
          {
            name: "Other",
            value: otherValue,
            children: otherItems,
          },
        ]
      : []),
  ];

  return finalData;
};
